import React from "react";
import {
  Calendar,
  CalendarX,
  Clock,
  ClockPlus,
  Euro,
  Info,
  MapPin,
  User,
  XCircle,
} from "lucide-react";
import { Link } from "react-router";
import {
  Subscription,
  useCancelTeacherSubscriptionMutation,
} from "../../api/subscriptionApi.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { weekdays } from "../Weekdays.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { format, parseISO } from "date-fns";
import ConfirmActionButton from "../ConfirmActionButton.tsx";
import { TooltipText } from "../TooltipText.tsx";

type DisplayStatus = Subscription["status"] | "CANCELLING";

function getDisplayStatus(subscription: Subscription): DisplayStatus {
  if (subscription.status === "ACTIVE" && subscription.endingAt) {
    return "CANCELLING";
  }
  return subscription.status;
}

function getLeftBorderClass(status?: DisplayStatus): string {
  switch (status) {
    case "INCOMPLETE":
      return "border-l-amber-400";
    case "ACCEPTED":
      return "border-l-soluud-secondary";
    case "ACTIVE":
      return "border-l-soluud-primary";
    case "CANCELLING":
      return "border-l-soluud-hint";
    case "PAST_DUE":
      return "border-l-soluud-warning";
    case "CANCELLED":
      return "border-l-soluud-border";
    default:
      return "border-l-soluud-border";
  }
}

function StatusPill({ status }: { status?: DisplayStatus }) {
  const base =
    "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-tight";
  switch (status) {
    case "INCOMPLETE":
      return (
        <span className={`${base} bg-amber-100 text-amber-700`}>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
          <Trans>Pending</Trans>
        </span>
      );
    case "ACCEPTED":
      return (
        <span
          className={`${base} bg-soluud-secondary-light text-soluud-text-on-secondary`}
        >
          <span className="bg-soluud-secondary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Accepted</Trans>
        </span>
      );
    case "ACTIVE":
      return (
        <span className={`${base} bg-soluud-primary/10 text-soluud-primary`}>
          <span className="bg-soluud-primary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Active</Trans>
        </span>
      );
    case "CANCELLING":
      return (
        <span className={`${base} bg-soluud-hint/10 text-soluud-hint`}>
          <span className="bg-soluud-hint h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Cancelling</Trans>
        </span>
      );
    case "PAST_DUE":
      return (
        <span className={`${base} bg-soluud-warning/10 text-soluud-warning`}>
          <span className="bg-soluud-warning h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Past Due</Trans>
        </span>
      );
    case "CANCELLED":
      return (
        <span
          className={`${base} bg-soluud-text-tertiary/10 text-soluud-text-tertiary`}
        >
          <span className="bg-soluud-text-tertiary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Cancelled</Trans>
        </span>
      );
    default:
      return null;
  }
}

interface TeacherSubscriptionCardProps {
  subscription: Subscription;
}

const TeacherSubscriptionCard: React.FC<TeacherSubscriptionCardProps> = ({
  subscription,
}) => {
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { i18n, t } = useLingui();
  const cancelMutation = useCancelTeacherSubscriptionMutation();
  const displayStatus = getDisplayStatus(subscription);

  const InstrumentIcon = getInstrumentIcon(subscription.instrument);
  const translatedInstrument = getTranslations(
    supportedInstruments ?? [],
    subscription.instrument,
    language,
  );

  const weekday = weekdays.find((w) => w.value === subscription.dayOfWeek);
  const translatedDay = weekday
    ? i18n._(weekday.label)
    : subscription.dayOfWeek;

  const fromTime = subscription.from?.substring(0, 5) ?? subscription.from;
  const toTime = subscription.to?.substring(0, 5) ?? subscription.to;

  const canWithdraw =
    subscription.status === "INCOMPLETE" || subscription.status === "ACCEPTED";

  const canCancel =
    (subscription.status === "ACTIVE" && !subscription.endingAt) ||
    subscription.status === "PAST_DUE";

  const handleCancel = () => {
    cancelMutation.mutate(subscription.id);
  };

  return (
    <div
      className={`bg-soluud-background-card border-soluud-border flex flex-col gap-2.5 rounded-xl border border-l-4 px-4 py-3 shadow-[--shadow-card] transition-shadow duration-200 hover:shadow-[--shadow-card-hover] ${getLeftBorderClass(displayStatus)}`}
    >
      {/* Row 1: Instrument + created date + status pill */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <InstrumentIcon className="text-soluud-primary h-4 w-4 shrink-0" />
          <span className="text-soluud-text-primary truncate text-sm font-semibold">
            {translatedInstrument || subscription.instrument}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {subscription.createdAt && (
            <span className="text-soluud-text-tertiary flex items-center gap-1 text-xs">
              <ClockPlus className="h-3 w-3 shrink-0" />
              <TooltipText
                tooltip={t`Created at ${format(new Date(subscription.createdAt), "Pp", { locale: dateFnsLocale })}`}
              >
                <span>
                  {format(new Date(subscription.createdAt), "PP", {
                    locale: dateFnsLocale,
                  })}
                </span>
              </TooltipText>
            </span>
          )}
          <StatusPill status={displayStatus} />
        </div>
      </div>

      {/* Row 2: Subscriber · Location · Schedule */}
      <div className="text-soluud-text-secondary flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5">
          {subscription.subscriberProfilePictureUrl ? (
            <img
              src={subscription.subscriberProfilePictureUrl}
              alt=""
              className="h-3.5 w-3.5 shrink-0 rounded-full object-cover"
            />
          ) : (
            <User className="h-3 w-3 shrink-0 opacity-50" />
          )}
          {subscription.subscriberName ? (
            <Link
              to={`/profile/${subscription.subscriberId}`}
              className="hover:text-soluud-primary hover:underline"
            >
              {subscription.subscriberName}
            </Link>
          ) : (
            <span className="text-soluud-text-tertiary">…</span>
          )}
        </span>

        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>

        <span className="flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 opacity-50" />
          <span className="truncate">{subscription.locationName ?? "…"}</span>
        </span>

        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>

        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3 shrink-0 opacity-50" />
          {translatedDay}, {fromTime} – {toTime}
        </span>
      </div>

      {/* Row 3: Price + billing / ending date + created date */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-1 text-xs">
          <Euro className="text-soluud-text-tertiary h-3 w-3 shrink-0" />
          {subscription.price != null ? (
            <>
              <span className="text-soluud-text-primary font-semibold">
                {subscription.price.toFixed(2)} €
              </span>
              <span className="text-soluud-text-tertiary">
                /<Trans>month</Trans>
              </span>
            </>
          ) : (
            <span className="text-soluud-text-tertiary italic">
              <Trans>TBD</Trans>
            </span>
          )}
        </span>

        {subscription.endingAt ? (
          <span className="text-soluud-hint flex items-center gap-1 text-xs">
            <CalendarX className="h-3 w-3 shrink-0" />
            {subscription.status === "CANCELLED" ? (
              <Trans>Ended</Trans>
            ) : (
              <Trans>Ends</Trans>
            )}
            &nbsp;
            {format(parseISO(subscription.endingAt), "PP")}
          </span>
        ) : (
          subscription.nextBillingAt &&
          subscription.status === "ACTIVE" && (
            <span className="text-soluud-text-secondary flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3 shrink-0 opacity-50" />
              <Trans>Next:</Trans>&nbsp;
              {format(new Date(subscription.nextBillingAt), "PP")}
            </span>
          )
        )}
      </div>

      {/* Cancellation reason */}
      {subscription.cancellationReason && (
        <div className="text-soluud-text-tertiary flex items-center gap-1.5 text-xs">
          <Info className="h-3 w-3 shrink-0" />
          {subscription.cancellationReason === "CANCELLED_BY_SUBSCRIBER" && (
            <Trans>Cancelled by subscriber</Trans>
          )}
          {subscription.cancellationReason === "CANCELLED_BY_TEACHER" && (
            <Trans>Cancelled by you</Trans>
          )}
          {subscription.cancellationReason === "TEACHER_ACCEPTANCE_TIMEOUT" && (
            <Trans>Cancelled: not accepted in time</Trans>
          )}
          {subscription.cancellationReason === "PAYMENT_TIMEOUT" && (
            <Trans>Cancelled: payment not added in time</Trans>
          )}
        </div>
      )}

      {/* Actions footer */}
      {(canCancel || canWithdraw) && (
        <div className="border-soluud-border-subtle flex items-center justify-end border-t pt-2.5">
          <ConfirmActionButton
            onConfirm={handleCancel}
            isLoading={cancelMutation.isPending}
            confirmText={
              canWithdraw
                ? t({ id: "Withdraw", message: "Withdraw" })
                : t({ id: "Cancel", message: "Cancel" })
            }
            cancelText={i18n._("Keep")}
            className="text-soluud-text-tertiary hover:bg-soluud-warning/5 hover:text-soluud-warning"
          >
            <XCircle className="h-3 w-3" />
            {canWithdraw
              ? t({ id: "Withdraw", message: "Withdraw" })
              : t({ id: "Cancel", message: "Cancel" })}
          </ConfirmActionButton>
        </div>
      )}
    </div>
  );
};

export default TeacherSubscriptionCard;
